import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import PipelineFileUpload from './PipelineFileUpload';
import { validateFileList } from '../../utils/pipelineHelpers';

function PipelineCreationForm({ projects, onSubmit, disabled }) {
    const { t } = useTranslation();
    const defaultFormData = {
        name: '',
        useExistingProject: false,
        projectUuid: '',
        researchDescription: '',
        files: [],
        articleRequirements: {
            title: '',
            description: '', // Article description
            pages: 15,
            bibliography_format: 'apa',
            language: 'en',
            articlePrompt: '' // Optional special instructions
        },
        researchContext: {
            researchQuestion: '',
            importanceRationale: '',
            participantsSelection: '',
            dataCollectionMethods: '',
            dataAnalysisMethods: '',
            limitations: ''
        },
        fallbackSearchTerms: ['']
    };

    // Load form data from sessionStorage on component mount
    const loadSavedFormData = () => {
        try {
            const savedData = sessionStorage.getItem('pipeline-creation-form');
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                // Ensure all required fields exist and files array is always empty on load
                return { 
                    ...defaultFormData, 
                    ...parsedData, 
                    files: [] // Always start with empty files since they can't be persisted
                };
            }
        } catch (error) {
            console.error('Error loading saved form data:', error);
        }
        return defaultFormData;
    };

    const [formData, setFormData] = useState(loadSavedFormData);

    // Save form data to sessionStorage whenever it changes (excluding files)
    useEffect(() => {
        try {
            // Don't save files since File objects can't be serialized
            const { files, ...dataToSave } = formData;
            sessionStorage.setItem('pipeline-creation-form', JSON.stringify(dataToSave));
        } catch (error) {
            console.error('Error saving form data:', error);
        }
    }, [formData]);

    // Clear saved form data (called after successful submission)
    const clearSavedFormData = () => {
        try {
            sessionStorage.removeItem('pipeline-creation-form');
        } catch (error) {
            console.error('Error clearing saved form data:', error);
        }
    };
    
    const [errors, setErrors] = useState({});

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleArticleRequirementChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            articleRequirements: {
                ...prev.articleRequirements,
                [field]: value
            }
        }));
    };

    const handleResearchContextChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            researchContext: {
                ...prev.researchContext,
                [field]: value
            }
        }));
    };

    const handleFilesChange = (files) => {
        setFormData(prev => ({
            ...prev,
            files: files
        }));
        
        // Clear file errors
        if (errors.files) {
            setErrors(prev => ({
                ...prev,
                files: null
            }));
        }
    };

    const handleSearchTermChange = (index, value) => {
        const newTerms = [...formData.fallbackSearchTerms];
        newTerms[index] = value;
        setFormData(prev => ({
            ...prev,
            fallbackSearchTerms: newTerms
        }));
    };

    const addSearchTerm = () => {
        setFormData(prev => ({
            ...prev,
            fallbackSearchTerms: [...prev.fallbackSearchTerms, '']
        }));
    };

    const removeSearchTerm = (index) => {
        if (formData.fallbackSearchTerms.length > 1) {
            const newTerms = formData.fallbackSearchTerms.filter((_, i) => i !== index);
            setFormData(prev => ({
                ...prev,
                fallbackSearchTerms: newTerms
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Basic validation
        if (!formData.name.trim()) {
            newErrors.name = t('ResearchPipelineForm.errors.nameRequired');
        }

        // Only validate project selection if useExistingProject is true
        if (formData.useExistingProject && !formData.projectUuid) {
            newErrors.projectUuid = t('ResearchPipelineForm.errors.projectRequired');
        }

        if (!formData.researchDescription.trim()) {
            newErrors.researchDescription = t('ResearchPipelineForm.errors.researchDescriptionRequired');
        }

        if (!formData.articleRequirements.title.trim()) {
            newErrors.articleTitle = t('ResearchPipelineForm.errors.articleTitleRequired');
        }

        // File validation
        const fileErrors = validateFileList(formData.files);
        if (fileErrors.length > 0) {
            newErrors.files = fileErrors;
        }

        // Search terms validation
        const validSearchTerms = formData.fallbackSearchTerms.filter(term => term.trim());
        if (validSearchTerms.length === 0) {
            newErrors.fallbackSearchTerms = t('ResearchPipelineForm.errors.searchTermsRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (validateForm()) {
            // Clean up search terms
            const cleanedData = {
                ...formData,
                fallbackSearchTerms: formData.fallbackSearchTerms.filter(term => term.trim())
            };
            
            onSubmit(cleanedData);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Basic Information */}
            <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('ResearchPipelineForm.basicInformation')}</h3>
                
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('ResearchPipelineForm.pipelineName')} *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                                errors.name ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder={t('ResearchPipelineForm.pipelineNamePlaceholder')}
                            disabled={disabled}
                        />
                        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('ResearchPipelineForm.researchDescription')} *
                    </label>
                    <textarea
                        value={formData.researchDescription}
                        onChange={(e) => handleInputChange('researchDescription', e.target.value)}
                        rows={4}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                            errors.researchDescription ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('ResearchPipelineForm.researchDescriptionPlaceholder')}
                        disabled={disabled}
                    />
                    {errors.researchDescription && <p className="text-red-500 text-sm mt-1">{errors.researchDescription}</p>}
                    <p className="text-sm text-gray-500 mt-1">
                        {t('ResearchPipelineForm.researchDescriptionHelp')}
                    </p>
                </div>
            </div>

            {/* File Upload */}
            <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('ResearchPipelineForm.files')}</h3>
                <PipelineFileUpload
                    files={formData.files}
                    onFilesChange={handleFilesChange}
                    disabled={disabled}
                />
                {errors.files && (
                    <div className="text-red-500 text-sm">
                        {errors.files.map((error, index) => (
                            <p key={index}>{error}</p>
                        ))}
                    </div>
                )}
            </div>

            {/* Article Requirements */}
            <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('ResearchPipelineForm.articleRequirements')}</h3>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('ResearchPipelineForm.articleTitle')} *
                    </label>
                    <input
                        type="text"
                        value={formData.articleRequirements.title}
                        onChange={(e) => handleArticleRequirementChange('title', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                            errors.articleTitle ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('ResearchPipelineForm.articleTitlePlaceholder')}
                        disabled={disabled}
                    />
                    {errors.articleTitle && <p className="text-red-500 text-sm mt-1">{errors.articleTitle}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('ResearchPipelineForm.articleDescription')}
                    </label>
                    <textarea
                        value={formData.articleRequirements.description}
                        onChange={(e) => handleArticleRequirementChange('description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        placeholder={t('ResearchPipelineForm.articleDescriptionPlaceholder')}
                        disabled={disabled}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                        {t('ResearchPipelineForm.articleDescriptionHelp')}
                    </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('ResearchPipelineForm.targetPages')}
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="50"
                            value={formData.articleRequirements.pages}
                            onChange={(e) => handleArticleRequirementChange('pages', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            disabled={disabled}
                        />
                    </div>
                    <div className="md:col-start-1">
                        {/* Empty div to maintain layout */}
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('ResearchPipelineForm.bibliographyFormat')}
                        </label>
                        <select
                            value={formData.articleRequirements.bibliography_format}
                            onChange={(e) => handleArticleRequirementChange('bibliography_format', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            disabled={disabled}
                        >
                            <option value="apa">APA</option>
                            <option value="mla">MLA</option>
                            <option value="chicago">Chicago</option>
                            <option value="harvard">Harvard</option>
                        </select>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('ResearchPipelineForm.language')}
                        </label>
                        <select
                            value={formData.articleRequirements.language}
                            onChange={(e) => handleArticleRequirementChange('language', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            disabled={disabled}
                        >
                            <option value="en">{t('ResearchPipelineForm.languageEnglish')}</option>
                            <option value="es">{t('ResearchPipelineForm.languageSpanish')}</option>
                            <option value="pt">{t('ResearchPipelineForm.languagePortuguese')}</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('ResearchPipelineForm.articlePrompt')}
                    </label>
                    <textarea
                        value={formData.articleRequirements.articlePrompt}
                        onChange={(e) => handleArticleRequirementChange('articlePrompt', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        placeholder={t('ResearchPipelineForm.articlePromptPlaceholder')}
                        disabled={disabled}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                        {t('ResearchPipelineForm.articlePromptHelp')}
                    </p>
                </div>
            </div>

            {/* Research Context (Optional) */}
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('ResearchPipelineForm.researchContext')}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                        {t('ResearchPipelineForm.researchContextSubtitle')}
                    </p>
                </div>
                
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('ResearchPipelineForm.researchQuestion')}
                        </label>
                        <textarea
                            value={formData.researchContext.researchQuestion}
                            onChange={(e) => handleResearchContextChange('researchQuestion', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder={t('ResearchPipelineForm.researchQuestionPlaceholder')}
                            disabled={disabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('ResearchPipelineForm.importanceRationale')}
                        </label>
                        <textarea
                            value={formData.researchContext.importanceRationale}
                            onChange={(e) => handleResearchContextChange('importanceRationale', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder={t('ResearchPipelineForm.importanceRationalePlaceholder')}
                            disabled={disabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('ResearchPipelineForm.participantsSelection')}
                        </label>
                        <textarea
                            value={formData.researchContext.participantsSelection}
                            onChange={(e) => handleResearchContextChange('participantsSelection', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder={t('ResearchPipelineForm.participantsSelectionPlaceholder')}
                            disabled={disabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('ResearchPipelineForm.dataCollectionMethods')}
                        </label>
                        <textarea
                            value={formData.researchContext.dataCollectionMethods}
                            onChange={(e) => handleResearchContextChange('dataCollectionMethods', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder={t('ResearchPipelineForm.dataCollectionMethodsPlaceholder')}
                            disabled={disabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('ResearchPipelineForm.dataAnalysisMethods')}
                        </label>
                        <textarea
                            value={formData.researchContext.dataAnalysisMethods}
                            onChange={(e) => handleResearchContextChange('dataAnalysisMethods', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder={t('ResearchPipelineForm.dataAnalysisMethodsPlaceholder')}
                            disabled={disabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('ResearchPipelineForm.limitations')}
                        </label>
                        <textarea
                            value={formData.researchContext.limitations}
                            onChange={(e) => handleResearchContextChange('limitations', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder={t('ResearchPipelineForm.limitationsPlaceholder')}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </div>

            {/* Fallback Search Terms */}
            <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('ResearchPipelineForm.fallbackSearchTerms')}</h3>
                <p className="text-sm text-gray-600">
                    {t('ResearchPipelineForm.fallbackSearchTermsHelp')}
                </p>
                
                <div className="space-y-3">
                    {formData.fallbackSearchTerms.map((term, index) => (
                        <div key={index} className="flex items-center gap-3">
                            <input
                                type="text"
                                value={term}
                                onChange={(e) => handleSearchTermChange(index, e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                                placeholder={t('ResearchPipelineForm.searchTermPlaceholder', { number: index + 1 })}
                                disabled={disabled}
                            />
                            {formData.fallbackSearchTerms.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeSearchTerm(index)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                    disabled={disabled}
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ))}
                    
                    <button
                        type="button"
                        onClick={addSearchTerm}
                        className="flex items-center gap-2 text-amber-500 hover:text-amber-800 text-sm"
                        disabled={disabled}
                    >
                        <PlusIcon className="w-4 h-4" />
                        {t('ResearchPipelineForm.addSearchTerm')}
                    </button>
                </div>
                
                {errors.fallbackSearchTerms && (
                    <p className="text-red-500 text-sm">{errors.fallbackSearchTerms}</p>
                )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                    type="submit"
                    disabled={disabled}
                    className="bg-plate-accent text-plate-ink px-8 py-3 rounded-lg hover:bg-plate-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {disabled ? t('ResearchPipelineForm.processing') : t('ResearchPipelineForm.createPipeline')}
                </button>
            </div>
        </form>
    );
}

export default PipelineCreationForm;